<template>
  <!-- SEO & Social Share Meta -->
  <Head :title="news.data.title">
    <meta property="og:title" :content="news.data.title" />
    <meta property="og:description" :content="description" />
    <meta property="og:image" :content="primaryImage" />
    <meta property="og:url" :content="canonicalUrl" />
    <meta property="og:type" content="article" />
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" :content="news.data.title" />
    <meta name="twitter:description" :content="description" />
    <meta name="twitter:image" :content="primaryImage" />
    <link rel="canonical" :href="canonicalUrl" />
  </Head>
  <Container :judul="news.title">
    <template #header>
      <HeaderFe />
    </template>
    <template v-slot:default>
      <main>
        <div
          class="max-w-[1170px] mx-auto px-2 sm:px-4 md:px-8 xl:px-0 pb-12 pt-10"
        >
          <div class="flex flex-col gap-4 sm:gap-6 lg:gap-8 lg:flex-row">
            <!-- Berita Utama -->
            <div class="flex-1">
              <div
                class="p-4 mb-6 bg-white shadow-sm sm:p-6 sm:mb-8 md:shadow-lg rounded-xl md:p-10"
              >
                <div class="mb-5">
                  <h1 class="mb-4 text-2xl font-bold sm:text-3xl text-dark">
                    {{ news.data.title }}
                  </h1>
                  <div
                    class="flex flex-wrap items-center gap-2 mt-2 text-sm text-gray-500"
                    v-if="news.data.author"
                  >
                    <span>
                      Oleh:
                      <span class="font-semibold text-dark">{{
                        news.data.author
                      }}</span>
                    </span>
                    <span class="flex items-center gap-1">
                      <i class="fa-solid fa-calendar-days"></i>
                      {{ formatTanggal(news.data.publish_date) }}
                    </span>
                  </div>
                </div>
                <!-- <div
                  class="mb-10"
                  v-if="news.data.image && news.data.image.length"
                >
                  <img
                    :src="news.data.image[0]"
                    alt="News Image"
                    class="object-cover w-full h-64 rounded-md md:h-96"
                  />
                </div> -->
                <div
                  v-html="news.data.content"
                  class="leading-relaxed text-gray-700 news-detail-content"
                ></div>
                <!-- Tags + Share -->
                <div
                  v-if="news.data.tags && news.data.tags.length"
                  class="mt-8"
                >
                  <div class="flex items-center justify-between mb-2">
                    <h3 class="font-semibold text-gray-700">Tags:</h3>
                    <div class="flex items-center gap-3">
                      <span class="text-xs text-gray-500">Bagikan:</span>
                      <a
                        :href="facebookShareUrl"
                        target="_blank"
                        class="text-blue-600 transition-colors hover:text-blue-800"
                        aria-label="Bagikan ke Facebook"
                        rel="noopener"
                      >
                        <i class="fa-brands fa-facebook-f"></i>
                      </a>
                      <a
                        :href="whatsappShareUrl"
                        target="_blank"
                        class="text-green-600 transition-colors hover:text-green-800"
                        aria-label="Bagikan ke WhatsApp"
                        rel="noopener"
                      >
                        <i class="fa-brands fa-whatsapp"></i>
                      </a>
                    </div>
                  </div>
                  <div class="flex flex-wrap gap-2">
                    <span
                      v-for="tag in news.data.tags"
                      :key="tag"
                      class="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full"
                    >
                      #{{ tag }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <!-- Sidebar -->
            <div class="flex-shrink-0 w-full lg:w-80">
              <div
                class="p-3 mb-6 bg-white shadow-sm sm:p-4 sm:mb-8 md:shadow rounded-xl"
              >
                <input
                  v-model="search"
                  type="text"
                  placeholder="Cari berita..."
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div
                class="p-3 mb-6 bg-white shadow-sm sm:p-4 sm:mb-8 md:shadow rounded-xl"
              >
                <h3 class="mb-4 font-semibold text-gray-700">Berita Lainnya</h3>

                <ul class="space-y-3">
                  <li v-for="item in beritaLainnya" :key="item.id">
                    <Link
                      :href="`/read/${item.slug}`"
                      class="block text-blue-700 hover:underline"
                    >
                      {{ item.title }}
                    </Link>
                    <div class="text-xs text-gray-400">
                      {{ formatTanggal(item.publish_date) }}
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>
    </template>
    <template #footer>
      <FooterFe />
    </template>
  </Container>
</template>

<script setup>
import { defineProps, ref, computed, onMounted } from "vue";
import { Link, Head, usePage } from "@inertiajs/vue3";
import Container from "@/Layouts/Container.vue";
import HeaderFe from "@/Components/HeaderFe.vue";
import FooterFe from "@/Components/FooterFe.vue";
import dayjs from "dayjs";
import "dayjs/locale/id";
dayjs.locale("id");
const props = defineProps({
  news: {
    type: Object,
    required: true,
  },
  otherNews: {
    type: [Array, Object],
    default: () => [],
  },
});
const formatTanggal = (tanggal) => {
  return tanggal ? dayjs(tanggal).format("D MMMM YYYY") : null;
};
const search = ref("");

// Base URL for absolute links (SSR-safe best effort)
const baseUrl = ref("");
onMounted(() => {
  if (typeof window !== "undefined" && window.location) {
    baseUrl.value = window.location.origin;
  }
});

// Canonical URL (path from Inertia + origin when available)
const page = usePage();
const canonicalUrl = computed(() => {
  const path = page.url?.split("#")[0] || "/";
  // Remove query params for canonical
  const cleanPath = path.split("?")[0];
  return baseUrl.value ? `${baseUrl.value}${cleanPath}` : cleanPath;
});

// Generate description from content if excerpt is missing
const stripHtml = (html) =>
  (html || "")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const description = computed(() => {
  const raw =
    props.news?.data && (props.news.data.excerpt || props.news.data.summary)
      ? props.news.data.excerpt || props.news.data.summary
      : stripHtml(props.news?.data?.content).slice(0, 180);
  return raw;
});

// Determine primary image from field or first image in content; ensure absolute URL
const resolveAbsolute = (src) => {
  if (!src) return "";
  if (/^https?:\/\//i.test(src)) return src;
  if (baseUrl.value) {
    if (src.startsWith("/")) return `${baseUrl.value}${src}`;
    return `${baseUrl.value}/${src}`;
  }
  return src; // fallback
};

const primaryImage = computed(() => {
  const imgField = props.news?.data?.image;
  let src = "";
  if (Array.isArray(imgField) && imgField.length) {
    src = imgField[0];
  } else if (typeof imgField === "string" && imgField.length) {
    src = imgField;
  } else if (typeof props.news?.data?.content === "string") {
    const match = props.news.data.content.match(
      /<img [^>]*src=["']([^"']+)["'][^>]*>/i
    );
    if (match && match[1]) src = match[1];
  }
  return resolveAbsolute(src);
});

// Share URLs (use server-rendered share page for better previews)
const sharePath = computed(() => `/share/${props.news?.data?.slug || ""}`);
const absoluteShareUrl = computed(() =>
  baseUrl.value ? `${baseUrl.value}${sharePath.value}` : sharePath.value
);
const facebookShareUrl = computed(() => {
  const url = encodeURIComponent(absoluteShareUrl.value);
  return `https://www.facebook.com/sharer/sharer.php?u=${url}`;
});
const whatsappShareUrl = computed(() => {
  const text = encodeURIComponent(
    `${props.news?.data?.title || ""} ${absoluteShareUrl.value}`.trim()
  );
  return `https://wa.me/?text=${text}`;
});

// Hanya gunakan props.otherNews dari backend
const beritaLainnya = computed(() => {
  // Jika otherNews adalah objek dengan properti data (Inertia resource collection)
  const arr = Array.isArray(props.otherNews)
    ? props.otherNews
    : props.otherNews && Array.isArray(props.otherNews.data)
    ? props.otherNews.data
    : [];
  const list = arr.filter((item) => item.id !== props.news.data.id);
  if (!search.value) return list;
  return list.filter((item) =>
    item.title.toLowerCase().includes(search.value.toLowerCase())
  );
});
</script>

<style scoped>
/* Support for Quill editor classes from backend */
.news-detail-content :deep(.ql-align-justify) {
  text-align: justify;
}

.news-detail-content :deep(.ql-align-center) {
  text-align: center;
}

.news-detail-content :deep(.ql-align-right) {
  text-align: right;
}

.news-detail-content :deep(.ql-align-left) {
  text-align: left;
}

/* Custom styles for HTML content - scoped only to news-detail-content */
.news-detail-content :deep(ol) {
  list-style-type: decimal;
  padding-left: 1.5rem;
  margin: 1rem 0;
}

.news-detail-content :deep(ul) {
  list-style-type: disc;
  padding-left: 1.5rem;
  margin: 1rem 0;
}

.news-detail-content :deep(ol li),
.news-detail-content :deep(ul li) {
  margin-bottom: 1rem;
  line-height: 1.6;
  padding-left: 0.5rem;
}

.news-detail-content :deep(ol li strong),
.news-detail-content :deep(ul li strong) {
  font-weight: 600;
  color: #1f2937;
}

/* Typography for paragraphs */
.news-detail-content :deep(p) {
  margin: 0.75rem 0;
  line-height: 1.7;
  color: #374151;
}

/* Additional typography support */
.news-detail-content :deep(h1),
.news-detail-content :deep(h2),
.news-detail-content :deep(h3),
.news-detail-content :deep(h4),
.news-detail-content :deep(h5),
.news-detail-content :deep(h6) {
  font-weight: 600;
  margin: 1.5rem 0 0.75rem 0;
  color: #1f2937;
}

.news-detail-content :deep(br) {
  line-height: 1;
}

/* Support for other Quill formatting */
.news-detail-content :deep(strong) {
  font-weight: 600;
}

.news-detail-content :deep(em) {
  font-style: italic;
}

.news-detail-content :deep(u) {
  text-decoration: underline;
}

/* Additional styling for better readability */
.news-detail-content :deep(blockquote) {
  border-left: 4px solid #e5e7eb;
  padding-left: 1rem;
  margin: 1rem 0;
  font-style: italic;
  color: #6b7280;
}

.news-detail-content :deep(code) {
  background-color: #f3f4f6;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Consolas,
    "Liberation Mono", Menlo, monospace;
  font-size: 0.875em;
}

.news-detail-content :deep(pre) {
  background-color: #f3f4f6;
  padding: 1rem;
  border-radius: 0.5rem;
  overflow-x: auto;
  margin: 1rem 0;
}

.news-detail-content :deep(img) {
  width: 100%;
  max-width: 100%;
  height: auto;
  min-height: 200px;
  max-height: 500px;
  object-fit: cover;
  object-position: center;
  border-radius: 0.5rem;
  margin: 1rem 0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

/* Responsive image sizing */
@media (max-width: 640px) {
  .news-detail-content :deep(img) {
    min-height: 150px;
    max-height: 300px;
    margin: 0.75rem 0;
    border-radius: 0.375rem;
  }
}

@media (min-width: 641px) and (max-width: 768px) {
  .news-detail-content :deep(img) {
    min-height: 180px;
    max-height: 400px;
  }
}

@media (min-width: 769px) {
  .news-detail-content :deep(img) {
    min-height: 200px;
    max-height: 500px;
  }
}

/* Hover effect for images */
.news-detail-content :deep(img:hover) {
  transform: scale(1.02);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  cursor: pointer;
}

/* Image container for better layout */
.news-detail-content :deep(p img) {
  display: block;
  margin-left: auto;
  margin-right: auto;
}

/* Multiple images in same paragraph */
.news-detail-content :deep(p) {
  text-align: justify;
}

.news-detail-content :deep(p:has(img)) {
  text-align: center;
}

/* Image figure and caption support */
.news-detail-content :deep(figure) {
  margin: 1.5rem 0;
  text-align: center;
}

.news-detail-content :deep(figure img) {
  margin-bottom: 0.5rem;
}

.news-detail-content :deep(figcaption) {
  font-size: 0.875rem;
  color: #6b7280;
  font-style: italic;
  text-align: center;
  margin-top: 0.5rem;
  padding: 0 1rem;
}

/* Loading placeholder for images */
.news-detail-content :deep(img[src=""]),
.news-detail-content :deep(img:not([src])) {
  background-color: #f3f4f6;
  background-image: linear-gradient(45deg, #e5e7eb 25%, transparent 25%),
    linear-gradient(-45deg, #e5e7eb 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #e5e7eb 75%),
    linear-gradient(-45deg, transparent 75%, #e5e7eb 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  animation: loading 1s linear infinite;
}

@keyframes loading {
  0% {
    background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  }
  100% {
    background-position: 20px 20px, 20px 30px, 30px 10px, 10px 20px;
  }
}

/* Image gallery styling for multiple images */
.news-detail-content :deep(.image-gallery) {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin: 1.5rem 0;
}

.news-detail-content :deep(.image-gallery img) {
  margin: 0;
  width: 100%;
  height: 200px;
  object-fit: cover;
}

/* Utility classes for different image alignments from editor */
.news-detail-content :deep(.ql-align-center img),
.news-detail-content :deep(.text-center img),
.news-detail-content :deep(img.center) {
  display: block;
  margin-left: auto;
  margin-right: auto;
}

.news-detail-content :deep(.ql-align-left img),
.news-detail-content :deep(.text-left img),
.news-detail-content :deep(img.left) {
  float: left;
  margin: 0 1rem 1rem 0;
  max-width: 50%;
}

.news-detail-content :deep(.ql-align-right img),
.news-detail-content :deep(.text-right img),
.news-detail-content :deep(img.right) {
  float: right;
  margin: 0 0 1rem 1rem;
  max-width: 50%;
}

/* Clear floats after floated images */
.news-detail-content :deep(.ql-align-left):after,
.news-detail-content :deep(.ql-align-right):after,
.news-detail-content :deep(.text-left):after,
.news-detail-content :deep(.text-right):after {
  content: "";
  display: table;
  clear: both;
}

/* Full width images */
.news-detail-content :deep(img.full-width) {
  width: 100vw;
  max-width: 100vw;
  margin-left: calc(-50vw + 50%);
  margin-right: calc(-50vw + 50%);
}

/* Small inline images */
.news-detail-content :deep(img.inline-small) {
  display: inline-block;
  max-width: 150px;
  max-height: 100px;
  margin: 0 0.5rem;
  vertical-align: middle;
}

/* Mobile specific adjustments */
@media (max-width: 640px) {
  .news-detail-content :deep(.image-gallery) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .news-detail-content :deep(.image-gallery img) {
    height: 180px;
  }

  .news-detail-content :deep(figcaption) {
    font-size: 0.8rem;
    padding: 0 0.5rem;
  }

  .news-detail-content :deep(.ql-align-left img),
  .news-detail-content :deep(.text-left img),
  .news-detail-content :deep(img.left),
  .news-detail-content :deep(.ql-align-right img),
  .news-detail-content :deep(.text-right img),
  .news-detail-content :deep(img.right) {
    float: none;
    display: block;
    margin: 1rem auto;
    max-width: 100%;
  }

  .news-detail-content :deep(img.full-width) {
    width: 100%;
    max-width: 100%;
    margin-left: 0;
    margin-right: 0;
  }

  .news-detail-content :deep(img.inline-small) {
    display: block;
    margin: 0.5rem auto;
  }
}
</style>
